import React, { useState } from 'react';
import {
  Shield,
  Download,
  Trash2,
  Database,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Server
} from 'lucide-react';
import { analyticsApi } from '@/services/api';
import { toast } from 'sonner';

interface BackupInfo {
  size: string;
  date: string;
  type: string;
}

export const SecurityPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [backupHistory, setBackupHistory] = useState<BackupInfo[]>([]);
  const [backupFormat, setBackupFormat] = useState<'json' | 'sql' | 'app'>('sql');
  const [systemStats, setSystemStats] = useState({
    reportFiles: 0,
    reportSize: '0 MB',
    tempFiles: 0,
    tempSize: '0 MB',
    logSize: '0 MB'
  });

  const handleBackup = async (format: 'json' | 'sql' | 'app') => {
    setIsLoading(`backup-${format}`);
    try {
      const response = await analyticsApi.createBackup(format);
      if ((response.data as any)?.success) {
        const { fileName, size } = (response.data as any).data;
        
        // Descargar backup
        const baseUrl = import.meta.env.VITE_API_URL.replace('/api/v1', '');
        const downloadResponse = await fetch(`${baseUrl}/uploads/reports/${fileName}`);
        const blob = await downloadResponse.blob();
        
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Actualizar historial
        const formatLabels = {
          json: 'JSON (Rápido)',
          sql: 'SQL Comprimido',
          app: 'Aplicación Completa'
        };
        
        const newBackup: BackupInfo = {
          size: typeof size === 'number' ? `${(size / 1024 / 1024).toFixed(2)} MB` : size,
          date: new Date().toLocaleString('es-ES'),
          type: formatLabels[format]
        };
        setBackupHistory(prev => [newBackup, ...prev.slice(0, 4)]);
        
        toast.success(`Backup ${formatLabels[format]} creado y descargado`);
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Error al crear el backup');
    } finally {
      setIsLoading(null);
    }
  };


  const handleCleanupReports = async () => {
    if (!confirm('¿Estás seguro de eliminar reportes antiguos (>7 días)?')) return;
    
    setIsLoading('cleanup-reports');
    try {
      const response = await analyticsApi.cleanupFiles({ type: 'reports' });
      if ((response.data as any)?.success) {
        const { deletedFiles, freedSpace } = (response.data as any).data;
        toast.success(`Eliminados ${deletedFiles} archivos, liberados ${freedSpace}`);
        await loadSystemStats();
      }
    } catch (error) {
      console.error('Error cleaning reports:', error);
      toast.error('Error al limpiar reportes');
    } finally {
      setIsLoading(null);
    }
  };

  const handleCleanupLogs = async () => {
    if (!confirm('¿Estás seguro de eliminar logs antiguos (>30 días)?')) return;
    
    setIsLoading('cleanup-logs');
    try {
      const response = await analyticsApi.cleanupFiles({ type: 'logs' });
      if ((response.data as any)?.success) {
        const { deletedFiles, freedSpace } = (response.data as any).data;
        toast.success(`Eliminados ${deletedFiles} archivos de log, liberados ${freedSpace}`);
        await loadSystemStats();
      }
    } catch (error) {
      console.error('Error cleaning logs:', error);
      toast.error('Error al limpiar logs');
    } finally {
      setIsLoading(null);
    }
  };

  const handleCleanupTemp = async () => {
    if (!confirm('¿Estás seguro de eliminar archivos temporales?')) return;
    
    setIsLoading('cleanup-temp');
    try {
      const response = await analyticsApi.cleanupFiles({ type: 'temp' });
      if ((response.data as any)?.success) {
        const { deletedFiles, freedSpace } = (response.data as any).data;
        toast.success(`Eliminados ${deletedFiles} archivos temporales, liberados ${freedSpace}`);
        await loadSystemStats();
      }
    } catch (error) {
      console.error('Error cleaning temp files:', error);
      toast.error('Error al limpiar archivos temporales');
    } finally {
      setIsLoading(null);
    }
  };

  const loadSystemStats = async () => {
    try {
      const response = await analyticsApi.getSystemStats();
      if ((response.data as any)?.success) {
        setSystemStats((response.data as any).data);
      }
    } catch (error) {
      console.error('Error loading system stats:', error);
    }
  };

  React.useEffect(() => {
    loadSystemStats();
  }, []);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <Shield className="w-6 h-6 mr-2 text-blue-600" />
            Seguridad y Mantenimiento
          </h1>
          <p className="text-gray-600">
            Gestión de backups y limpieza del sistema
          </p>
        </div>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Reportes</p>
              <p className="text-2xl font-bold text-gray-900">{systemStats.reportFiles}</p>
              <p className="text-xs text-gray-500">{systemStats.reportSize}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <HardDrive className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Temporales</p>
              <p className="text-2xl font-bold text-gray-900">{systemStats.tempFiles}</p>
              <p className="text-xs text-gray-500">{systemStats.tempSize}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Server className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Logs</p>
              <p className="text-2xl font-bold text-gray-900">Sistema</p>
              <p className="text-xs text-gray-500">{systemStats.logSize}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Database className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-600">Estado</p>
              <p className="text-lg font-bold text-green-600 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                Activo
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Backup Section */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Download className="w-5 h-5 mr-2 text-green-600" />
            Respaldos (Backups)
          </h2>

          {/* Backup Format Selection */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Formato de Backup</h3>
              <p className="text-sm text-gray-600">Selecciona el tipo de backup a crear</p>
            </div>
            
            <div className="space-y-3">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="backupFormat"
                  value="json"
                  checked={backupFormat === 'json'}
                  onChange={(e) => setBackupFormat(e.target.value as 'json' | 'sql' | 'app')}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-gray-900">JSON (Rápido)</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Datos legibles para inspección</p>
                </div>
              </label>
              
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="backupFormat"
                  value="sql"
                  checked={backupFormat === 'sql'}
                  onChange={(e) => setBackupFormat(e.target.value as 'json' | 'sql' | 'app')}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-gray-900">SQL Comprimido (Recomendado)</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Base de datos restaurable (.sql.gz)</p>
                </div>
              </label>
              
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="backupFormat"
                  value="app"
                  checked={backupFormat === 'app'}
                  onChange={(e) => setBackupFormat(e.target.value as 'json' | 'sql' | 'app')}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="flex items-center">
                    <HardDrive className="w-5 h-5 text-purple-600 mr-2" />
                    <span className="font-medium text-gray-900">Aplicación Completa</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Sistema completo con archivos (.tar.gz)</p>
                </div>
              </label>
            </div>
            
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="text-sm text-gray-500">
                {backupFormat === 'json' && 'Incluye: datos de usuario, cursos, pagos'}
                {backupFormat === 'sql' && 'Incluye: base de datos PostgreSQL completa'}
                {backupFormat === 'app' && 'Incluye: BD, archivos, configuración, script restore.sh'}
              </div>
              <button
                onClick={() => handleBackup(backupFormat)}
                disabled={isLoading?.startsWith('backup')}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center font-medium"
              >
                {isLoading?.startsWith('backup') ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Creando {backupFormat.toUpperCase()}...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    {backupFormat === 'app' ? 'Crear Backup Completo' : 'Crear Backup'}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Format Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">JSON</span>
              </div>
              <p className="text-sm text-blue-800">Rápido y legible para inspección</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Database className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-green-900">SQL</span>
              </div>
              <p className="text-sm text-green-800">Base de datos restaurable comprimida</p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <HardDrive className="w-5 h-5 text-purple-600 mr-2" />
                <span className="font-medium text-purple-900">Aplicación</span>
              </div>
              <p className="text-sm text-purple-800">Sistema completo funcional</p>
            </div>
          </div>

          {/* Backup History */}
          {backupHistory.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Backups Recientes</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {backupHistory.map((backup, index) => (
                  <div key={index} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{backup.type}</p>
                      <p className="text-sm text-gray-500">{backup.date}</p>
                    </div>
                    <div className="text-sm text-gray-600">{backup.size}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Cleanup Section */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Trash2 className="w-5 h-5 mr-2 text-red-600" />
            Limpieza del Sistema
          </h2>

          <div className="space-y-4">
            {/* Reports Cleanup */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FileText className="w-6 h-6 text-orange-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Reportes Antiguos</h3>
                    <p className="text-sm text-gray-600">Eliminar reportes mayores a 7 días</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-600" />
                  Elimina archivos PDF, Excel y CSV antiguos
                </div>
                <button
                  onClick={handleCleanupReports}
                  disabled={isLoading === 'cleanup-reports'}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isLoading === 'cleanup-reports' ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Limpiando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Limpiar
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Temp Files Cleanup */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <HardDrive className="w-6 h-6 text-red-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Archivos Temporales</h3>
                    <p className="text-sm text-gray-600">Limpiar cache y archivos temporales</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Libera espacio en disco del sistema
                </div>
                <button
                  onClick={handleCleanupTemp}
                  disabled={isLoading === 'cleanup-temp'}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isLoading === 'cleanup-temp' ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Limpiando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Limpiar
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Logs Cleanup */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Server className="w-6 h-6 text-gray-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Logs del Sistema</h3>
                    <p className="text-sm text-gray-600">Eliminar logs mayores a 30 días</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-600" />
                  Mantiene logs recientes para debug
                </div>
                <button
                  onClick={handleCleanupLogs}
                  disabled={isLoading === 'cleanup-logs'}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isLoading === 'cleanup-logs' ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Limpiando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Limpiar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Safety Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Importante</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Las operaciones de limpieza son irreversibles. 
                  Se recomienda crear backups antes de limpiar archivos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};